import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CheckboxModule } from 'primeng/checkbox';
import { RadioButtonModule } from 'primeng/radiobutton';
import { SelectModule } from 'primeng/select';
import { TagModule } from 'primeng/tag';

import {
  ProductTaxInheritedValue,
  ProductTaxMode,
  ProductTaxOption,
  ProductTaxValue,
} from './product-tax.model';
import {
  findProductTaxOption,
  DEFAULT_PRODUCT_TAX_VALUE,
  formatProductTaxCategoryDisplay,
  resolveProductTaxDisplayValue,
} from './product-tax.util';

const PRODUCT_TAX_MODE_OPTIONS: {
  label: string;
  value: ProductTaxMode;
}[] = [
  { label: 'goods.tax.mode.inherit', value: 'INHERIT' },
  { label: 'goods.tax.mode.override', value: 'OVERRIDE' },
];

@Component({
  selector: 'app-product-tax-control',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CheckboxModule,
    RadioButtonModule,
    SelectModule,
    TagModule,
  ],
  templateUrl: './product-tax-control.component.html',
  styleUrl: './product-tax-control.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ProductTaxControlComponent {
  @Input() value: ProductTaxValue = DEFAULT_PRODUCT_TAX_VALUE;
  @Input() inheritedValue: ProductTaxInheritedValue | null = null;
  @Input() taxOptions: ProductTaxOption[] = [];
  @Input() disabled = false;
  @Input() compact = false;
  @Input() idPrefix = 'product-tax';
  @Output() valueChange = new EventEmitter<ProductTaxValue>();

  readonly modeOptions = PRODUCT_TAX_MODE_OPTIONS;

  get displayValue(): ProductTaxValue {
    return resolveProductTaxDisplayValue(this.value, this.inheritedValue);
  }

  get isOverrideMode(): boolean {
    return this.value.mode === 'OVERRIDE';
  }

  get controlsDisabled(): boolean {
    return this.disabled || !this.isOverrideMode;
  }

  get inheritedTaxCategoryLabel(): string {
    const option = findProductTaxOption(this.taxOptions, this.displayValue.taxId);
    if (option) {
      return option.label;
    }

    return formatProductTaxCategoryDisplay(
      {
        taxCode: this.inheritedValue?.taxCode,
        taxName: null,
      },
      '',
    );
  }

  get inheritedVatKey(): string {
    if (this.displayValue.isPriceIncludeVAT === true) {
      return 'common.options.yes';
    }
    if (this.displayValue.isPriceIncludeVAT === false) {
      return 'common.options.no';
    }
    return 'goods.tax.unconfigured';
  }

  get sourceKey(): string | null {
    const source = this.inheritedValue?.source;
    return source ? `goods.tax.source.${source.toLowerCase()}` : null;
  }

  onModeChange(mode: ProductTaxMode): void {
    if (mode === 'INHERIT') {
      this.valueChange.emit({
        mode,
        taxId: null,
        isPriceIncludeVAT: null,
      });
      return;
    }

    this.valueChange.emit({
      mode,
      taxId: this.value.taxId,
      isPriceIncludeVAT: this.value.isPriceIncludeVAT ?? false,
    });
  }

  onTaxIdChange(taxId: number | null): void {
    this.valueChange.emit({
      ...this.value,
      mode: 'OVERRIDE',
      taxId,
      isPriceIncludeVAT: this.value.isPriceIncludeVAT ?? false,
    });
  }

  onVatChange(event: { checked?: boolean }): void {
    this.valueChange.emit({
      ...this.value,
      mode: 'OVERRIDE',
      isPriceIncludeVAT: event.checked === true,
    });
  }
}
